import { useCallback, useState } from 'react';
import { useTagSearch } from './useTagSearch';
import type { TagChip } from '../components/ChatInput/ChatInput.types';
import type { TicketTag } from '../utils/cabTagSerializer';

/** One entry of `GET /tickets/:projectId/tags/suggest` — a ticket or an epic. */
interface TicketTagSuggestion {
  type?: string;
  label: string;
  ticketId?: string;
  status?: string;
  epicId?: string;
  epicCode?: string;
  wave?: number;
}

/**
 * Suggest response → ChatInput chips. Kept at module level so its identity is
 * stable: useTagSearch's handler lists `mapResponse` as a dependency.
 */
const mapTicketTagSuggestions = (data: any): TagChip[] =>
  (data?.suggestions || []).map((s: TicketTagSuggestion) =>
    s.type === 'epic'
      ? {
          id: s.epicId as string,
          label: s.label,
          sectionName: 'Epics',
          data: { type: 'epic', epicId: s.epicId, epicCode: s.epicCode, wave: s.wave },
        }
      : {
          id: s.ticketId as string,
          label: s.label,
          sectionName: 'Tickets',
          data: { type: 'ticket', ticketId: s.ticketId, status: s.status },
        }
  );

/**
 * Build-mode ticket / epic @-reference chips: the suggest search plus the
 * selected-tag state that both Build chats serialize into `{{ticket:…}}` /
 * `{{epic:…}}` tokens (see cabTagSerializer.serializeTicketTagsIntoContent).
 *
 * Shared by the web project's chat (ChatPage) and the low-code project's
 * (FedRampBuildChat) so the chip shape stays one thing — the two agents resolve
 * the tokens differently, but a tagged message looks the same in both inputs.
 */
export function useTicketTagChips(projectId?: string, token?: string | null) {
  const [selectedTicketTags, setSelectedTicketTags] = useState<TicketTag[]>([]);

  const { suggestions, isSearching, handleTagSearch } = useTagSearch({
    endpoint: projectId ? `/tickets/${projectId}/tags/suggest` : null,
    token,
    mapResponse: mapTicketTagSuggestions,
  });

  const handleTicketTagsChange = useCallback((tagChips: TagChip[]) => {
    setSelectedTicketTags(
      tagChips.map((chip) =>
        chip.data?.type === 'epic'
          ? { id: chip.id, label: chip.label, type: 'epic' as const, epicId: chip.data?.epicId || chip.id }
          : { id: chip.id, label: chip.label, type: 'ticket' as const, ticketId: chip.data?.ticketId || chip.id }
      )
    );
  }, []);

  return {
    selectedTicketTags,
    setSelectedTicketTags,
    ticketTagSuggestions: suggestions,
    isTicketTagSearching: isSearching,
    handleTicketTagSearch: handleTagSearch,
    handleTicketTagsChange,
  };
}
